import { Link } from "react-router-dom";
import { ListPageHeader, ResultsCard, formatResultCount } from "../../common/DataList";
import SuiviAnomalyListFilters from "./SuiviAnomalyListFilters/SuiviAnomalyListFilters";
import useSuiviAnomalyList from "./useSuiviAnomalyList";
import tools from "../../../helpers/tools";
import { DateTime } from "luxon";
import { useEffect } from "react";

const COUNT_LABELS = {
  none: "Aucune anomalie trouvée",
  one: "anomalie trouvée",
  many: "anomalies trouvées",
};

function SuiviAnomalyList() {
  const {
    filteredAnomalies: anomalies,
    paginatedAnomalies,

    constants,
    isLoading,
    errors,
    actions,
    nbResults,
    nbPages,
    pagination,
  } = useSuiviAnomalyList();

  useEffect(() => {
    actions.searchData();
  }, []);

  // Les filtres s'appliquent à la liste complète chargée d'un coup : le
  // compteur rappelle le total quand ils la réduisent.
  const constantesChargees = !!constants.ANOMALY_TYPES;
  const compteur =
    formatResultCount(anomalies.length, COUNT_LABELS) +
    (nbResults && anomalies.length !== nbResults ? ` sur ${nbResults.toLocaleString("fr-FR")}` : "");

  return (
    <div className="container-fluid px-5 my-5 list-page">
      <ListPageHeader
        title="Suivi BRSA SDD sans RDO ou sans RDV EG"
        subtitle="Cette liste affiche la totalité des anomalies. La recherche initiale est automatique et peut prendre un peu de temps, mais vous pouvez ensuite filtrer les résultats en temps réel."
      />
      {errors && errors.other && (
        <div className="alert alert-danger" role="alert">
          {errors.other}
        </div>
      )}
      <SuiviAnomalyListFilters
        constants={constants}
        isLoading={isLoading}
        actions={actions}
      />
      <ResultsCard
        caption="Suivi des BRSA SDD sans RDO ou sans RDV EG"
        rows={constantesChargees ? paginatedAnomalies : []}
        count={anomalies.length}
        countText={compteur}
        isLoading={isLoading || !constantesChargees}
        error={errors && errors.other}
        page={pagination.page}
        nbPages={nbPages}
        nbItemsByPage={pagination.nbItemsByPage}
        onChangePage={actions.changePage}
        onChangeNbItemsByPage={actions.changeNbItemsByPage}
        itemLabel="anomalies"
        exportOptions={
          constantesChargees
            ? {
                datas: anomalies.map((r) => ({
                  ...r,
                  name: tools.getFullname(r),
                  recipientAddress: `${r.address || ""} ${
                    r.location?.locationName || ""
                  } ${r.location?.postalCode || ""}`,
                  recipientType: tools.getRecipientTypeLibelle(r.model),
                  RDSPPresente: r.RDSPPresente ? "Oui" : "Non",
                  rdvEGPresent: r.rdvEGPresent ? "Oui" : "Non",
                  typeAnomalie: tools.findIn(
                    constants.ANOMALY_TYPES,
                    "id",
                    r.typeAnomalie
                  )?.name,
                  dateEnvoiNotif1: r.dateEnvoiNotif1
                    ? tools.formatDate(r.dateEnvoiNotif1)
                    : "",
                  dateEnvoiCourrierJ16: r.dateEnvoiCourrierJ16
                    ? tools.formatDate(r.dateEnvoiCourrierJ16)
                    : "",
                  dateEnvoiCourrierJ16Reelle: r.dateEnvoiCourrierJ16Reelle
                    ? tools.formatDate(r.dateEnvoiCourrierJ16Reelle)
                    : "Non envoyé",
                  suspendedMonth: r.moisSuspension
                    ? tools.formatDate(r.moisSuspension, "MMMM yyyy")
                    : "",
                })),

                excludedFields: ["Voir"],
                objectFields: {
                  location: {
                    dataIndexes: ["name"],
                  },
                },
                additionalFields: [
                  { name: "Adresse allocataire", path: "recipientAddress" },
                  { name: "NIR", path: "nir" },
                  { name: "Matr.", path: "CAFId" },
                  { name: "Type alloc", path: "recipientType" },
                  {
                    name: "Date envoi courrier J+16 réelle",
                    path: "dateEnvoiCourrierJ16Reelle",
                  },
                ],
              }
            : null
        }
        fields={[
          {
            name: "Allocataire",
            path: "*",
            exportPath: "name",
            render: (elem) => (
              <>
                <div
                  className={`badge badge-${tools.getRecipientTypeColor(
                    elem.model
                  )}`}
                >
                  {tools.getRecipientTypeLibelle(elem.model)}
                </div>
                <a
                  target="_blank"
                  rel="noreferer"
                  href={tools.getRecipientLink(elem)}
                  className="btn btn-link text-left"
                >
                  {tools.getFullname(elem)}
                </a>
                <small className="d-block">NIR : {elem.nir}</small>
                <small className="d-block">Matr. : {elem.CAFId}</small>
                <small className="d-block">{elem.email}</small>
              </>
            ),
          },
          {
            name: "Date envoi 1ère solicitation",
            path: "*",
            exportPath: "dateEnvoiNotif1",
            render: (elem) =>
              elem.dateEnvoiNotif1
                ? DateTime.fromISO(elem.dateEnvoiNotif1).toFormat(
                    "dd/MM/yyyy"
                  )
                : "",
          },
          {
            name: "Date envoi du courrier J+16",
            path: "*",
            exportPath: "dateEnvoiCourrierJ16",
            render: (elem) => {
              return (
                <>
                  <div>
                    <small className="d-block font-weight-bold">
                      Prévue :
                    </small>
                    <small className="d-block">
                      {elem.dateEnvoiCourrierJ16
                        ? DateTime.fromISO(
                            elem.dateEnvoiCourrierJ16
                          ).toFormat("dd/MM/yyyy")
                        : ""}
                    </small>
                  </div>
                  <div>
                    <small className="d-block font-weight-bold">
                      Réelle :{" "}
                    </small>
                    <small className="d-block">
                      {elem.dateEnvoiCourrierJ16Reelle ? (
                        DateTime.fromISO(
                          elem.dateEnvoiCourrierJ16
                        ).toFormat("dd/MM/yyyy")
                      ) : (
                        <i className="text-danger">Non envoyé</i>
                      )}
                    </small>
                  </div>
                </>
              );
            },
          },
          {
            name: "Mois de suspension",
            path: "*",
            exportPath: "suspendedMonth",
            render: (elem) =>
              tools.formatDate(elem.moisSuspension, "MMMM yyyy"),
          },
          {
            name: "Type anomalie",
            exportPath: "typeAnomalie",
            path: "typeAnomalie",
            render: (elem) => (
              <small>
                {
                  tools.findIn(constants.ANOMALY_TYPES, "id", elem)
                    ?.name
                }
              </small>
            ),
          },
          {
            name: "Type de droits",
            exportPath: "typeDroits",
            path: "typeDroits",
          },
          {
            name: "RDO",
            path: "RDSPPresente",
            exportPath: "RDSPPresente",
            render: (elem) => (
              <div
                className={`badge badge-${elem ? "success" : "danger"}`}
              >
                <i className={`fa fa-${elem ? "check" : "times"}`} />
              </div>
            ),
          },
          {
            name: "RDV EG",
            path: "rdvEGPresent",
            exportPath: "rdvEGPresent",
            render: (elem) => (
              <div
                className={`badge badge-${elem ? "success" : "danger"}`}
              >
                <i className={`fa fa-${elem ? "check" : "times"}`} />
              </div>
            ),
          },
          {
            name: "Voir",
            path: "*",
            render: (elem) => (
              <a
                target="_blank"
                rel="noreferer"
                href={tools.getRecipientLink(elem)}
                className="btn btn-primary w-100 btn-sm d-flex align-items-center justify-content-center"
              >
                <i className="fa fa-eye"></i>
              </a>
            ),
          },
        ]}
      />
    </div>
  );
}

export default SuiviAnomalyList;
